import React, { useState, useEffect } from "react";
import { Card, Box, CircularProgress, Typography } from "@mui/material";
import api from "../../services/api";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import CakeInfoSection from "./CakeInfoSection";
import CakeOptionsForm from "./CakeOptionsForm";
import AddToCartButton from "./AddToCartButton";
import SnackbarAlert from "../../components/SnackbarAlert";
import ProductImageGallery from "../../components/ProductImageGallery";

const StandardCakeCard = ({
  cake,
  setShowCartPreview,
  isProcessing,
  setIsProcessing,
  isAvailable = true,
}) => {
  const [flavors, setFlavors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [selectedFlavor, setSelectedFlavor] = useState("");
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [primaryImage, setPrimaryImage] = useState("");
  const [secondaryImages, setSecondaryImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [fullScreenOpen, setFullScreenOpen] = useState(false);
  const [imagesReady, setImagesReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "warning",
  });

  const { addToCart } = useCart();
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    fetchCakeSpecificDetails();
  }, [cake]);

  const fetchCakeSpecificDetails = async () => {
    try {
      const [flavorsRes, sizesRes] = await Promise.all([
        api.get("/flavors/by-ids", { params: { ids: cake.flavors } }),
        api.get("/sizes/by-ids", { params: { ids: cake.sizes } }),
      ]);

      setFlavors(flavorsRes.data);
      setSizes(sizesRes.data);

      if (flavorsRes.data.length > 0) {
        const isHoneyCake = cake.name.toLowerCase().includes("honey");

        if (isHoneyCake) {
          const raspberryFlavor = flavorsRes.data.find((flavor) =>
            flavor.displayName.toLowerCase().includes("raspberry")
          );
          if (raspberryFlavor) {
            setPrimaryImage(raspberryFlavor.images.primary);
          }

          const honeyCakeImages = flavorsRes.data.map(
            (flavor) => flavor.images.primary
          );
          if (honeyCakeImages.length > 0) {
            setSecondaryImages(honeyCakeImages);
          }
        } else {
          const firstFlavor = flavorsRes.data[0];
          setPrimaryImage(firstFlavor.images.primary);
          setSecondaryImages(firstFlavor.images.secondary || []);
        }
      }

      const price = sizes[0]?.price || 0;

      if (window.fbq && cake && flavors.length > 0 && sizes.length > 0) {
        window.fbq("track", "ViewContent", {
          content_name: cake.displayName,
          content_ids: [cake._id],
          content_type: "product",
          value: price,
          currency: "CAD",
        });
      }

      if (window.gtag) {
        window.gtag("event", "view_item", {
          currency: "CAD",
          value: price,
          items: [
            {
              item_id: cake._id,
              item_name: cake.displayName,
              item_category: cake.category || "standardCake",
              price,
            },
          ],
        });
      }
    } catch (error) {
      console.error("Error fetching cake options:", error);
    }
  };

  useEffect(() => {
    const ready =
      primaryImage &&
      typeof primaryImage === "string" &&
      primaryImage.length > 0 &&
      Array.isArray(secondaryImages);
    setImagesReady(ready);
  }, [primaryImage, secondaryImages]);

  const handleAddToCart = async () => {
    if (isProcessing) return;

    if (
      (sizes.length > 0 && !selectedSize) ||
      (flavors.length > 1 && !selectedFlavor) ||
      (cake.colors?.length > 1 && !selectedColor)
    ) {
      setAlert({
        open: true,
        message: "Please select all options before adding to cart!",
        severity: "warning",
      });
      return;
    }

    setIsProcessing(true);
    setIsLoading(true);

    setTimeout(async () => {
      setIsLoading(false);
      setIsProcessing(false);
      setShowCartPreview(true);

      let selectedFlavorId =
        typeof selectedFlavor === "object"
          ? selectedFlavor._id
          : selectedFlavor;

      if (!selectedFlavorId && cake.flavors.length === 1) {
        selectedFlavorId =
          typeof cake.flavors[0] === "object"
            ? cake.flavors[0]._id
            : cake.flavors[0];
      }

      const selectedFlavorData = flavors.find(
        (f) => f._id.toString() === selectedFlavorId.toString()
      );
      const selectedSizeData = sizes.find((s) => s._id === selectedSize);
      const unitPrice = selectedSizeData?.price || 0;

      const flavorName = selectedFlavorData?.displayName || "";

      const newItem = {
        _id: cake._id,
        category: cake.category || "standardCake",
        subcategory: null,
        name: cake.name,
        displayName: cake.displayName,
        image: selectedFlavorData?.images?.primary || "",
        size: selectedSizeData
          ? { _id: selectedSizeData._id, name: selectedSizeData.displayName }
          : null,
        flavor: selectedFlavorData
          ? { _id: selectedFlavorData._id, name: flavorName }
          : null,
        customizations: [
          selectedSizeData && {
            key: "size",
            label: "Size",
            value: selectedSizeData.displayName,
          },
          selectedFlavorData && {
            key: "flavor",
            label: "Flavor",
            value: flavorName,
          },
          selectedColor && {
            key: "color",
            label: "Color",
            value: selectedColor,
          },
        ].filter(Boolean),
        unitPrice,
        quantity: 1,
        totalPrice: unitPrice,
      };

      addToCart(newItem);

      if (window.fbq) {
        window.fbq("track", "AddToCart", {
          content_name: cake.displayName,
          content_ids: [cake._id],
          content_type: "product",
          value: unitPrice,
          currency: "CAD",
        });
      }

      if (window.gtag) {
        window.gtag("event", "add_to_cart", {
          currency: "CAD",
          value: unitPrice,
          items: [
            {
              item_id: cake._id,
              item_name: cake.displayName,
              item_category: cake.category || "standardCake",
              item_variant: selectedSizeData?.displayName || "",
              item_flavor: flavorName || "",
              item_color: selectedColor || "",
              quantity: 1,
              price: unitPrice,
            },
          ],
        });
      }

      if (user && isAuthenticated) {
        try {
          await api.post("/cart/add", newItem);
        } catch (err) {
          console.error("Cart sync failed:", err);
        }
      }
    }, 1000);
  };

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: "500px",
      }}>
      <Box sx={{ minHeight: 300, position: "relative" }}>
        {!imagesReady ? (
          <Box
            sx={{
              height: 350,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 4,
            }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 2 }}>
              Loading cake images...
            </Typography>
          </Box>
        ) : (
          <ProductImageGallery
            images={[primaryImage, ...(secondaryImages || [])]}
            productName={cake.displayName}
            dietaryTags={flavors?.flatMap((f) => f.dietaryTags || [])}
            thumbnailPosition="right"
            defaultIndex={selectedImageIndex}
            onIndexChange={setSelectedImageIndex}
            fullScreenOpen={fullScreenOpen}
            setFullScreenOpen={setFullScreenOpen}
          />
        )}
      </Box>

      <CakeInfoSection cake={cake} flavors={flavors} />

      <CakeOptionsForm
        sizes={sizes}
        flavors={flavors}
        colors={cake.colors || []}
        selectedSize={selectedSize}
        setSelectedSize={setSelectedSize}
        selectedFlavor={selectedFlavor}
        setSelectedFlavor={setSelectedFlavor}
        selectedColor={selectedColor}
        setSelectedColor={setSelectedColor}
      />

      {isAvailable ? (
        <AddToCartButton
          isProcessing={isProcessing}
          isLoading={isLoading}
          handleAddToCart={handleAddToCart}
        />
      ) : (
        <Box
          sx={{
            textAlign: "center",
            py: 2,
            backgroundColor: "#f8d7da",
            borderTop: "1px solid #f5c2c7",
          }}>
          <Typography variant="body1" color="error" fontWeight="bold">
            Temporarily Unavailable
          </Typography>
          <Typography variant="body2">
            This cake is currently not available for ordering.
          </Typography>
        </Box>
      )}

      <SnackbarAlert
        open={alert.open}
        onClose={() => setAlert({ ...alert, open: false })}
        message={alert.message}
        severity={alert.severity}
        bgColor={alert.severity === "warning" ? "#FF9800" : "#4CAF50"}
        iconColor="white"
      />
    </Card>
  );
};

export default StandardCakeCard;
